import React, { useState, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ROOMLIST, ROOMPRICING } from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const roomSchema = z.object({
  roomId: z.string().nonempty("Room is required"),
  ratePricing: z
    .array(
      z.object({
        durationType: z.string().nonempty("Duration type is required"),
        basePrice: z.number().min(0, "Base price must be >= 0"),
        discount: z.number().min(0).max(100),
        refundable: z.boolean(),
      })
    )
    .min(1, "Enter at least one rate"),
});

const AddRoomPrice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const{t}=useTranslation()
  const [loader, setLoader] = useState(false);
  const [hotelAdminData, setHotelAdminData] = useState([]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      hotelId: "",
      numberOfBed: 1,
      ratePricing: [
        { durationType: "", basePrice: 0, discount: 0, refundable: true },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ratePricing",
  });

  const getAmenityData = async () => {
    try {
      const roomRes = await ROOMLIST({ limit: 100, page: 1 });
      if (roomRes?.data?.status === "success") {
        setHotelAdminData(roomRes?.data?.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getAmenityData();
  }, []);

  const submitHandler = async (data) => {
    console.log(data, "payload data====<>");
    setLoader(true);
    try {
      let res = await ROOMPRICING(data);
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message);
        navigate("/room");
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => navigate("/room");

 
  return (
    <>
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/room"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {"Add Room Pricing"}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>Select Room Type</label>
                      <select className="form-control" {...register("roomId")}>
                        {/* <option value="">-- Select Room --</option> */}
                        {hotelAdminData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin?.roomType?.name}
                          </option>
                        ))}
                      </select>
                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>Number of Beds</label>
                      <input
                        type="number"
                        className="form-control"
                        {...register("numberOfBed", { valueAsNumber: true })}
                      />
                      {errors.numberOfBed && (
                        <p className="text-danger">
                          {errors.numberOfBed.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="12" className="my-2">
                      <h6>Rate Pricing</h6>
                      {fields.map((field, index) => (
                        <Row key={field.id} className="align-items-center mb-3">
                          <Col md={3}>
                            <label>Duration Type</label>
                            <input
                              type="text"
                              className="form-control"
                              {...register(`ratePricing.${index}.durationType`)}
                            />
                            {errors.ratePricing?.[index]?.durationType && (
                              <p className="text-danger">
                                {
                                  errors.ratePricing[index].durationType
                                    ?.message
                                }
                              </p>
                            )}
                          </Col>

                          <Col md={2}>
                            <label>Base Price</label>
                            <input
                              type="number"
                              className="form-control"
                              {...register(`ratePricing.${index}.basePrice`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Col>

                          <Col md={2}>
                            <label>Discount %</label>
                            <input
                              type="number"
                              className="form-control"
                              {...register(`ratePricing.${index}.discount`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Col>

                          <Col md={2} className="d-flex align-items-center">
                            <Form.Check
                              type="checkbox"
                              label="Refundable"
                              {...register(`ratePricing.${index}.refundable`)}
                            />
                          </Col>

                          <Col md={2} className="d-flex align-items-center">
                            <Button
                              variant="danger"
                              onClick={() => remove(index)}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                      ))}

                      <Button
                        variant="primary"
                        onClick={() =>
                          append({
                            durationType: "",
                            basePrice: 0,
                            discount: 0,
                            refundable: true,
                          })
                        }
                      >
                        Add Rate
                      </Button>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoomPrice;
